import clsx from "clsx";
import { parse } from "csv-parse/sync";
import { twMerge } from "tailwind-merge";
import { ControlsGeoType, ControlsProps, ControlsStatType, ControlsViewType, ControlsYearType, LocationProps } from "./types";
import { DATA_VERSION, CONTROLS_GEO_KEYS, LOCATION_ID_LENGTHS, CONTROLS_STAT_YEARS } from "./constants";
import { getAllStatsOfView, getControlOptionsByType, getLang, getUnit } from "./selectors";
/** Gets a value key from given controls */
export const getValueKey = (controls: ControlsProps) =>
	`${controls.view}_${controls.stat}_${controls.year}`;
/** Gets value from object if exists */
export const getValue = (key: string, object: any) =>
	object && object.hasOwnProperty(key) ? (object as any)[key] : null;
/** Get the location type based on the ID length */
export const getLocationType = (id: string) =>
	CONTROLS_GEO_KEYS.find((key: string) =>
		getValue(key, LOCATION_ID_LENGTHS) === id?.length
	) as ControlsGeoType;
/** Get two-digit state FIPS from location object */
export const getLocationStateFips = (id: string) => String(id)?.slice(0, 2);
/** Get full name of state from location object */
export const getLocationStateName = (id: string) => getLang("state", getLocationStateFips(id));
/** Get two-character state code from location object */
export const getLocationStateCode = (id: string) => getLang("state", getLocationStateFips(id), "short");
/** Get the location name, formatting for census tracts */
export const getLocationName = (geo: ControlsGeoType, id: string) =>
	geo === "tract"
		? `Census Tract ${id}`
		: `${getLang(geo, id)}`
/** Get the location name with comma-separated state code */
export const getLocationNameWithStateCode = (geo: ControlsGeoType, id: string) =>
	`${getLocationName(geo, id)}, ${getLocationStateCode(id)}`
/** Get the location name with comma-separated state name */
export const getLocationNameWithStateName = (geo: ControlsGeoType, id: string) =>
	`${getLocationName(geo, id)}, ${getLocationStateName(id)}`
/** Format stat value */
export const formatValue = (value: number | null | undefined, stat: ControlsStatType) => {
	if(value === null || value == undefined) return "N/A";
	const unit = getUnit(stat);
	if(unit === "per") {
		return `${value}%`;
	} else {
		return value;
	}
}
/** Format location ID based on length of geo type by padding leading zeroes */
export const formatLocationId = (id: string | number, geo: ControlsGeoType) =>
	String(id).padStart(getValue(geo, LOCATION_ID_LENGTHS), "0")
/** Perform valid update of a control object */
export const isValidValueOfType = (type: string, value: any, view?: ControlsViewType) => {
	if(value && type !== "stat") {
		return getControlOptionsByType(type)?.includes(value)
	} else if(view && value && type === "stat" && view) {
		return getAllStatsOfView(view)?.includes(value)
	} else {
		return false;
	}
}
/** Validates a control value */
export const validateNewControlValue = (type: string, value: string | number, controls: ControlsProps) => {
	const newControls = {
		...controls,
	} as any;
	if(isValidValueOfType(type, value, newControls.view)) {
		newControls[type] = value;
		// If changing stat, also save value in stats
		if(type === "stat") {
			newControls.stats[newControls.view] = newControls.stat;
		}
		// If changing year, also save value in years
		if(type === "year") {
			newControls.years[newControls.view] = newControls.year;
		}
		// If changing view, update stat and year to value saved in stats and years
		if(type === "view") {
			newControls.stat = newControls.stats[newControls.view];
			newControls.year = newControls.years[newControls.view];
		}
		// If changing stat or view, update year
		if(type === "stat" || type === "view") {
			// Get valid year options of selected view and stat
			const yearsForStat = getValue(`${newControls.view}_${newControls.stat}`, CONTROLS_STAT_YEARS);
			// If invalid year, update to default valid year for selected view and stat
			const newYear = Array.isArray(yearsForStat) && yearsForStat.length > 0
					? yearsForStat[0]
					: undefined;
			if(!yearsForStat?.includes(newControls.year)) {
				newControls.year = newYear;
				newControls.years[newControls.view] = newYear;
			}
		}
	}
	return newControls;
}
/** Creates extents with steps */
export const createExtents = (extents: [number | undefined, number | undefined]) => {
	const [min, max] = extents;
	if(min === undefined || max === undefined) return []
	const steps = 6;
	const delta = (max - min) / (steps - 1);
	const newExtents = Array.from({
		length: steps
	}, (step, index) =>
		Math.round((min + index * delta) * 100) / 100
	);
	return newExtents.filter(x => x !== null);
};
/** Checks if URL matches origin of current site or is another */
export const isExternalUrl = (url: string) => {
	let currentOrigin = process.env.NEXT_PUBLIC_BASE_URL;
	try {
		const parsedURL = new URL(url);
		// If currentOrigin is not provided, try to infer it from the document
		if (!currentOrigin && typeof document !== 'undefined') {
			currentOrigin = window.location.origin;
		}
		if (!currentOrigin) {
			// If currentOrigin is still not available, and url is absolute, assume it is external.
			return parsedURL.origin !== (parsedURL.protocol + "//" + parsedURL.hostname);
		}
		return parsedURL.origin !== currentOrigin;
	} catch (_error) {
		// If URL parsing fails, it's likely not a valid URL, so treat it as internal.
		return false;
	}
}
/**
 * Replaces a string's placeholder values based on a key/value match in provided object
 * (i.e. "Replace ${this} value" with { this: value })
*/
export const replaceStringWithParams = (
	input: string,
	params: object
) => {
	return input
		?.split(/(\{[a-zA-Z0-9_]*\})/)
		?.map(str => {
			if (str && str[0] !== '{') return str
			str = str.replace('{', '').replace('}', '')
			return params && params.hasOwnProperty(str) ? (params as any)[str] : str
		})
		?.join('')
};
/** Fetch data from internal Next.js API */
export async function fetchApi(
	geo: ControlsGeoType,
	// type: "meta" | "report" | string,
	id?: string
) {
	const params: any = { geo };
	if(id) params.id = id;
	const urlParams = new URLSearchParams(params);
	const url = `${process.env.NEXT_PUBLIC_BASE_URL}/api/data/?${urlParams.toString()}`;
	try {
		const response = await fetch(url);
		return response.json();
	} catch (error) {
		console.error(`Error fetching API data from ${url}:`, error);
	}
};
/** Fetch data from remote source */
export async function fetchData(path: string, id?: string) {
	const filetype = "csv";
	let url = `https://${process.env.NEXT_PUBLIC_AWS_S3_BUCKET}.s3.${process.env.NEXT_PUBLIC_AWS_REGION}.amazonaws.com/${process.env.NEXT_PUBLIC_AWS_S3_DIRECTORY}/`;
	if(id) {
		url += `${path}/${id}.${filetype}`;
	} else {
		url += `${path}.${filetype}`;
	}
	try {
		const requestStart = performance.now();
		const response = await fetch(`${url}?version=${DATA_VERSION}`);
		const data = await handleCsv(response);
		const requestEnd = performance.now();
		const requestDur = Math.round(requestEnd - requestStart) / 1000;
		console.log(`Fetched ${data.length} records in ${requestDur} seconds`)
		// If fetching using ID argument, return only first object, otherwise return array
		return id ? data[0] : data;
	} catch (error) {
		console.error(`Error handling data from ${url}:`, error);
		return id ? [] : {};
	}
};
/** Parse JSON from data source */
export async function handleJson(response: Response) {
	const data = await response.json();
	return data;
};
/** Parse CSV from data source */
export async function handleCsv(response: Response) {
	try {
		const csv = await response.text();
		const data = parse(csv, {
			columns: true,
			skip_empty_lines: true,
			cast: (value, context) => {
				// Empty values become null
				if (value === "") return null;
				// Make sure the id column is cast as a string
				if (context.column === "id" || context.column === "name") return String(value);
				// Everything else is a number (or null if empty)
				return isNaN(Number(value)) ? value : Number(value);
			},
		});
		return data;
	} catch (error) {
		console.error(`Error parsing CSV:`, error);
		return [];
	}
};
/** Gets URL parameters as a control objext */
export const getUrlParams = () => {
	const newHashKeys = window.location.hash.replace("#/", "").split("/");
	const [newView, newStat, newGeo, newYear, newId] = newHashKeys;
	const params = {
		view: newView as ControlsViewType,
		stat: newStat as ControlsStatType,
		geo: newGeo as ControlsGeoType,
		year: newYear as ControlsYearType,
		id: newId as LocationProps["id"],
	};
	return params;
}
/** Creates a URL from control params */
export const createParamUrl = (params: any) => {
	const { view, stat, geo, year, id } = params;
	let url = `${window.location.origin}/#/${view}/${stat}/${geo}/${year}`;
	if(id) url = `${url}/${id}`;
	return url;
}
/** Updates the favicon to given view type */
export const updateFavicons = (view: string) => {
	document.body.setAttribute("data-view", view);
	const newIco = `/favicons/favicon-${view}.ico`;
	const newPng = `/favicons/favicon-${view}.png`;
	const newSvg = `/favicons/favicon-${view}.svg`;
	const newApple = `/favicons/apple-icon-${view}.png`;
	const icoTags = document.querySelectorAll("[rel=\"icon\"][type=\"image\/x-icon\"]");
	const pngTags = document.querySelectorAll("[rel=\"icon\"][type=\"image\/png\"]");
	const svgTags = document.querySelectorAll("[rel=\"icon\"][type=\"image\/svg\+xml\"]");
	const appleTags = document.querySelectorAll("[rel=\"apple-touch-icon\"]");
	icoTags.forEach(tag => tag?.setAttribute("href", newIco));
	pngTags.forEach(tag => tag?.setAttribute("href", newPng));
	svgTags.forEach(tag => tag?.setAttribute("href", newSvg));
	appleTags.forEach(tag => tag?.setAttribute("href", newApple));
}
/** Joins classnames and passes through clsx */
export const cn = (
	...arr: (string | object | null | undefined)[]
) => {
	const classNames = arr
		.filter(item => item)
		.map(item =>
			typeof item === "object" ?
				clsx(item)
			: clsx(twMerge(item))
		)
	return clsx(classNames);
};